import React, { useState, useRef, useEffect, useCallback } from 'react';
import LazyImage from '../atoms/LazyImage';
import { getResponsiveImage } from '../../utils/responsiveImage';

const supportsNativeDialog = () =>
  typeof HTMLDialogElement !== 'undefined' &&
  typeof HTMLDialogElement.prototype.showModal === 'function';

const thumbSizes = '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw';

const PhotoGallery = ({ photos = [], title, className = '' }) => {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [liveAnnouncement, setLiveAnnouncement] = useState('');
  const dialogRef = useRef(null);
  const openerRef = useRef(null);
  const scrollLockRef = useRef({ locked: false, previous: '' });

  const selectedPhoto = selectedIndex !== null ? photos[selectedIndex] : null;

  const lockScroll = () => {
    const state = scrollLockRef.current;
    if (!state.locked) {
      state.previous = document.body.style.overflow;
      state.locked = true;
    }
    document.body.style.overflow = 'hidden';
  };

  const unlockScroll = () => {
    const state = scrollLockRef.current;
    if (!state.locked) {
      return;
    }
    document.body.style.overflow = state.previous;
    state.locked = false;
  };

  const buildAnnouncement = useCallback(
    (index) => {
      if (index === null || index < 0 || index >= photos.length) {
        return '';
      }
      return `Image ${index + 1} of ${photos.length}: ${photos[index].alt}`;
    },
    [photos]
  );

  const openModal = (index, buttonEl) => {
    if (!photos.length) {
      return;
    }

    if (!supportsNativeDialog()) {
      const photo = photos[index];
      const href = photo.modalSrc || getResponsiveImage(photo.src).modalSrc || photo.src;
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }

    openerRef.current = buttonEl;
    setSelectedIndex(index);
    setLiveAnnouncement(buildAnnouncement(index));
  };

  const closeModal = () => {
    dialogRef.current?.close();
  };

  const nextPhoto = () => {
    setSelectedIndex((current) => {
      const nextIndex = (current + 1) % photos.length;
      setLiveAnnouncement(buildAnnouncement(nextIndex));
      return nextIndex;
    });
  };

  const prevPhoto = () => {
    setSelectedIndex((current) => {
      const prevIndex = (current - 1 + photos.length) % photos.length;
      setLiveAnnouncement(buildAnnouncement(prevIndex));
      return prevIndex;
    });
  };

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || !dialog.showModal) {
      return undefined;
    }

    const handleClose = () => {
      unlockScroll();
      setSelectedIndex(null);
      setLiveAnnouncement('');
      openerRef.current?.focus();
    };

    dialog.addEventListener('close', handleClose);

    return () => {
      dialog.removeEventListener('close', handleClose);
    };
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || !dialog.showModal) {
      return undefined;
    }

    if (selectedIndex === null) {
      if (dialog.open) {
        dialog.close();
      } else {
        unlockScroll();
      }
      return undefined;
    }

    if (!dialog.open) {
      dialog.showModal();
    }
    lockScroll();

    return undefined;
  }, [selectedIndex]);

  useEffect(() => () => unlockScroll(), []);

  if (!photos.length) {
    return null;
  }

  return (
    <div className={className}>
      {title && (
        <h3 className="text-2xl font-semibold mb-6 text-center">{title}</h3>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {photos.map((photo, index) => {
          const responsive = getResponsiveImage(photo.src);
          const thumbSrcSet =
            photo.srcSet ||
            (responsive.srcSet
              ? responsive.srcSet
                  .split(', ')
                  .filter((entry) => {
                    const width = parseInt(entry.split(' ')[1], 10);
                    return width <= 800;
                  })
                  .join(', ')
              : undefined);

          return (
            <button
              key={`${photo.src}-${index}`}
              type="button"
              className="relative overflow-hidden rounded-lg text-left transform transition-all duration-300 hover:scale-105 hover:shadow-lg motion-reduce:transform-none motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow focus-visible:ring-offset-2"
              onClick={(event) => openModal(index, event.currentTarget)}
              aria-label={`Open ${photo.alt}`}
            >
              <LazyImage
                src={responsive.src}
                srcSet={thumbSrcSet}
                sizes={photo.sizes || thumbSizes}
                alt={photo.alt}
                className="w-full h-48 object-cover pointer-events-none"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all duration-300 motion-reduce:transition-none flex items-center justify-center pointer-events-none">
                <svg
                  className="w-8 h-8 text-white opacity-0 hover:opacity-100 transition-opacity duration-300 motion-reduce:transition-none"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M3 4a1 1 0 011-1h12a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm1 0v12h12V4H4zm4 8a2 2 0 100-4 2 2 0 000 4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              {photo.caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white p-2 text-sm pointer-events-none">
                  {photo.caption}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {supportsNativeDialog() && (
        <dialog
          ref={dialogRef}
          className="fixed inset-0 z-50 m-0 max-w-none max-h-none w-full h-full bg-transparent p-4 backdrop:bg-black/90 open:flex open:items-center open:justify-center"
          aria-labelledby="gallery-dialog-title"
          onCancel={(event) => {
            event.preventDefault();
            closeModal();
          }}
        >
          <div aria-live="polite" aria-atomic="true" className="sr-only">
            {liveAnnouncement}
          </div>
          {selectedPhoto && (
            <div className="relative max-w-4xl max-h-full w-full">
              <h2 id="gallery-dialog-title" className="sr-only">
                {selectedPhoto.alt}
              </h2>
              <img
                src={
                  selectedPhoto.modalSrc || getResponsiveImage(selectedPhoto.src).modalSrc
                }
                alt={selectedPhoto.alt}
                className="max-w-full max-h-[80vh] object-contain rounded-lg mx-auto"
              />

              <button
                type="button"
                onClick={closeModal}
                aria-label="Close"
                className="absolute top-4 right-4 text-white text-xl w-8 h-8 bg-black bg-opacity-50 rounded-full flex items-center justify-center hover:bg-opacity-70 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow"
              >
                ×
              </button>

              {photos.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={prevPhoto}
                    aria-label="Previous image"
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white text-2xl w-10 h-10 bg-black bg-opacity-50 rounded-full flex items-center justify-center hover:bg-opacity-70 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow"
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    onClick={nextPhoto}
                    aria-label="Next image"
                    className="absolute top-1/2 right-4 transform -translate-y-1/2 text-white text-2xl w-10 h-10 bg-black bg-opacity-50 rounded-full flex items-center justify-center hover:bg-opacity-70 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow"
                  >
                    →
                  </button>
                </>
              )}

              {selectedPhoto.caption && (
                <div className="absolute bottom-4 left-4 right-4 text-white text-center bg-black bg-opacity-50 p-3 rounded">
                  {selectedPhoto.caption}
                </div>
              )}
            </div>
          )}
        </dialog>
      )}
    </div>
  );
};

export default PhotoGallery;
